import React, { useMemo, useEffect, useRef, useState } from 'react';
// import {
//   ActivityIndicator,
// } from 'tamagui';
import { useNotifications } from '../../hooks/useNotifications';
import { Notification, NotificationListItem } from '@cobu/types';
import { CobuContainer, CobuStack, CobuText, CobuHeader, CobuBackButton, CobuButton, CobuLoader, CobuAlert } from '@cobu/ui';
import { Strings } from '../../constants/Strings';
import { NotificationItem } from '../../components/events/NotificationItem';
import { Virtuoso } from 'react-virtuoso';
import { useNavigate } from 'react-router-dom';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import { useThemeName } from 'tamagui';
import { showErrorMessage, showSuccessMessage } from '../../utils/messageToast';
import { PullToRefreshWrapper } from '../../components/ui/PullToRefreshWrapper';

const strings = Strings.get();

export default function NotificationsScreen() {
  const {
    notificationGroups,
    totalNotifications,
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,
    hasMoreData,
    handleRefresh,
    handleLoadMore,
    handleMarkAllAsRead,
    handleNotificationPress,
  } = useNotifications();
  const navigate = useNavigate();
  const themeName = useThemeName();
  const [showMarkAllConfirm, setShowMarkAllConfirm] = useState(false);

  const flattenedData: NotificationListItem[] = useMemo(() => {
    const items: NotificationListItem[] = [];
    notificationGroups.forEach((group) => {
      if (group.data.length > 0) {
        items.push({ type: 'header', title: group.title, key: `header-${group.title}` });
        group.data.forEach((notification: Notification, index: number) => {
          items.push({ type: 'notification', data: notification, key: `${notification.uuid}-${index}` });
        });
      }
    });
    return items;
  }, [notificationGroups]);

  const stickyHeaderIndices = React.useMemo(() => {
    return flattenedData
      .map((item, index) => item.type === 'header' ? index : -1)
      .filter(index => index !== -1);
  }, [flattenedData]);

  // Wire header actions from AppShell (must be before any early returns)
  useEffect(() => {
    const onMarkAll = () => {
      setShowMarkAllConfirm(true);
    };
    window.addEventListener('notif:markAll', onMarkAll)
    return () => window.removeEventListener('notif:markAll', onMarkAll)
  }, [])

  // Error state
  const hasShownErrorRef = useRef(false);
  useEffect(() => {
    if (error && !hasShownErrorRef.current) {
      showErrorMessage(strings.common.somethingWentWrong);
      hasShownErrorRef.current = true;
    }
  }, [error]);

  const hasAnyNotifications = totalNotifications > 0;

  if (isLoading && !hasAnyNotifications) {
    return (
      <CobuContainer f={1} jc="center" ai="center" bg="$background">
        {/* <ActivityIndicator size="large" color={theme.cobuTeal.val} /> */}
        <CobuLoader size="large" color="$cobuTeal" />
      </CobuContainer>
    );
  }

  if (error) {
    return null;
  }

  return (
    <CobuContainer f={1} bg="$background">
      <CobuHeader
        title={strings.notifications.title}
        renderLeft={() => <CobuBackButton onPress={() => navigate(-1)} />}
        renderRight={() => (
          <CobuButton backgroundColor="transparent" onPress={() => window.dispatchEvent(new CustomEvent('notif:markAll'))}>
            <DoneAllOutlinedIcon fontSize="small" htmlColor={themeName === 'entrata' ? '#000000' : '#ffffff'} />
          </CobuButton>
        )}
      />
      {hasAnyNotifications ? (
        <CobuStack f={1}>
          <PullToRefreshWrapper
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
            useWindowScroll={true}
          >
            <Virtuoso
              data={flattenedData}
              itemContent={(index, item) => renderItem({ item } as any)}
              computeItemKey={(index, item) => item.key}
              components={{
                Footer: () => <>{renderFooter()}</>,
              }}
              // endReached={hasMoreData ? handleLoadMore : undefined}
              overscan={400}
              useWindowScroll
              style={{ width: '100%' }}
            />
          </PullToRefreshWrapper>
        </CobuStack>
      ) : (
        <CobuContainer f={1} jc="center" ai="center" p="$4">
          <CobuText color="$color" fos="$4" ta="center">
            {strings.notifications.emptyState}
          </CobuText>
        </CobuContainer>
      )}

      {/* Mark all as read confirmation */}
      <CobuAlert
        open={showMarkAllConfirm}
        onOpenChange={setShowMarkAllConfirm}
        title={strings.notifications.markAllAsRead.title}
        description={strings.notifications.markAllAsRead.message}
        acceptText={strings.common.buttonLabels.yes}
        cancelText={strings.common.buttonLabels.no}
        onAccept={async () => {
          setShowMarkAllConfirm(false);
          const ok = (await handleMarkAllAsRead()) as boolean;
          if (ok) {
            showSuccessMessage(strings.notifications.messages.allMarkedAsRead);
          } else {
            showErrorMessage(strings.notifications.messages.markAllFailed);
          }
        }}
        onCancel={() => setShowMarkAllConfirm(false)}
      />
    </CobuContainer>
  );

  function renderItem({ item }: { item: NotificationListItem }) {
    if (item.type === 'header') {
      return (
        <CobuStack
          backgroundColor="$white"
          px="$4"
          py="$2"
        >
          <CobuText color="$cobuDarkBlue" fontSize="$3" fontWeight="bold">
            {item.title?.toUpperCase() || ''}
          </CobuText>
        </CobuStack>
      );
    }

    return (
      <NotificationItem
        notification={item.data as Notification}
        onPress={handleNotificationPress}
      />
    );
  }

  function renderFooter() {
    if (isLoadingMore) {
      return (
        <CobuContainer py="$4" ai="center">
          <CobuLoader size="small" color="$cobuTeal" />
        </CobuContainer>
      );
    }

    if (hasMoreData) {
      return (
        <CobuContainer ai="center" py="$4">
          <CobuButton dsVariant="secondary" title={strings.notifications.loadOlder} onPress={handleLoadMore} />
        </CobuContainer>
      );
    }

    return <CobuStack h="$4" />;
  }
}
